import { computed, inject, Injectable, signal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { ChatMessagePayload, HubEvent, IMessageForm } from '../models/SupportSystem.Model';
import { environment } from '../environment';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class SignalRService {  
  apiService = inject(ApiService);
  hubConnectionUrl:string = `${environment.SiteBaseUrl}SystemSupportService`;
  //private hubConnection!: signalR.HubConnection;
  private hubConnection: signalR.HubConnection | null = null;
  private isConnecting = false; // Flag to prevent concurrent connection loops
  
  // Internal writeable signal tracking incoming messages
  private _messageReceived = signal<ChatMessagePayload | null>(null);
  
  // Publicly exposed read-only signal that components listen to
  public newMessageReceived = computed(() => this._messageReceived());

  // Exposing clean, separated signals for your application components to safely compute against
  public presenceEvent = signal<{ userId: string; userName: string; type: string } | null>(null);
  public groupMessageEvent = signal<ChatMessagePayload | null>(null);
  public systemNoticeEvent = signal<{ roomName: string; message: string } | null>(null);

  constructor() {}

  // --- EXPLICIT CONNECT METHOD ---
  public async connect(): Promise<void> {
    // Prevent double execution if already connected or currently trying to connect
    if (this.isConnecting || (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected)) {
      return;
    }   

    this.isConnecting = true;
    
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.hubConnectionUrl, {
        //skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,        
        accessTokenFactory: async () => {
          try {           
            const jwtToken = await firstValueFrom(this.apiService.browserStorageService.get(this.apiService.browserStorageService.tokenKey),{ defaultValue: null });
            return jwtToken ? jwtToken : '';
          } catch (error) {

            return '';
          }
        }
      })
      .configureLogging(signalR.LogLevel.None)
      //.configureLogging(signalR.LogLevel.Trace)
      // Handles automatic reconnects IF an established connection drops later
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) 
      .build();

    // Register events  
    this.hubConnection.on('ReceiveHubEvent', (payload: HubEvent) => {
      const HubEventObj :any = { ...payload.Data, type: payload.EventType };
      //console.log('ReceiveHubEvent', payload);
      switch (payload.EventType) {
        case 'UserConnected':
        case 'UserDisconnected':
          this.presenceEvent.set(HubEventObj);
          break;
        case 'NewGroupMessage':
          this.groupMessageEvent.set({ ...payload.Data});
          break;
        case 'SystemNotice':
          this.systemNoticeEvent.set(HubEventObj);
          break;
      }
    });

    this.hubConnection.on('ReceiveGlobalMessage', (data: ChatMessagePayload) => {
      // Update the reactive signal state
      this._messageReceived.set(data);
    });

    await this.startConnectionWithRetry();
  }

  // --- INITIAL CONNECTION RETRY LOOP ---
  private async startConnectionWithRetry(): Promise<void> {
    if (!this.hubConnection) return;

    try {
      await this.hubConnection.start();
      //console.log('Global SignalR Engine Connected Successfully!');
      this.isConnecting = false;      
      // Auto-join default room now that wire is active
      //this.joinRoom('General-Lounge');
    } catch (err) {

      // Wait 5 seconds and attempt to spin up again recursively
      setTimeout(() => this.startConnectionWithRetry(), 5000);
    }
  }

  // --- EXPLICIT DISCONNECT METHOD ---
  public async disconnect(): Promise<void> {
    if (this.hubConnection) {
      try {
        await this.hubConnection.stop();
        //console.log('hub Connection Explicitly Terminated.');
      } catch (err) {

      } finally {
        this.hubConnection = null;
        this.isConnecting = false;
      }
    }
  }
  

  // --- OUTBOUND CLIENT HUB METHOD INVOCATIONS ---  
  public joinRoom(roomName: string): void {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection.invoke('JoinRoom', roomName)
        .catch(err => console.error(`Failed joining room ${roomName}:`, err));
    }else{
     //console.log('joinRoom hubConnection undefined ::', this.hubConnection)      
    }
  }
  
  public leaveRoom(roomName: string): void {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection.invoke('LeaveRoom', roomName)
        .catch(err => console.error(`Failed leaving room ${roomName}:`, err));
    }else{
     //console.log('leaveRoom hubConnection undefined ::', this.hubConnection)
    }
  }
  
  public sendGroupMessage(roomName: string, messageBody: IMessageForm, languageCode:string="EN"): void {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {    
      //console.log("send messageBody ::", JSON.stringify(messageBody));  
      this.hubConnection.invoke('SendGroupMessage', roomName, JSON.stringify(messageBody), languageCode)
        .catch(err => console.error('Failed delivering rich message frame:', err));
    }else{
     //console.log('sendGroupMessage hubConnection undefined ::', this.hubConnection)
    }
  }

}
